import { forwardRef, useState } from 'react';

import type { MantineTheme } from '@mantine/core';
import {
  Accordion,
  Checkbox,
  Group,
  Popover,
  Stack,
  Text,
} from '@mantine/core';
import { useClickOutside, useDisclosure } from '@mantine/hooks';

import { IconSelector } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../../shared/components/UI/Button/Button';
import { Counter } from '../../../../shared/components/UI/Counter/Counter';

import { CheckBoxListProvider, useCheckBoxList } from './CheckBoxListContext';

type CheckBoxListProps = {
  label: string;
  count: number;
  children: React.ReactNode;
  customTarget?: React.ReactNode;
  popoverWidth?: string;
  withinPortal?: boolean;
};

type CheckBoxData = {
  label: string | React.ReactNode;
  value: string;
};

type CheckBoxDataWithChildren = CheckBoxData & {
  children: CheckBoxData[];
};

export function CheckBoxList({
  label,
  count,
  children,
  customTarget,
  popoverWidth,
  withinPortal = false,
}: CheckBoxListProps) {
  const [inputRef, setInputRef] = useState<HTMLButtonElement | null>(null);
  const [customTargetRef, setCustomTargetRef] = useState<HTMLDivElement | null>(
    null,
  );
  const [dropdownRef, setDropdownRef] = useState<HTMLDivElement | null>(null);
  const [opened, { close, open, toggle }] = useDisclosure(false);
  useClickOutside(() => close(), null, [
    inputRef,
    dropdownRef,
    customTargetRef,
  ]);

  return (
    <CheckBoxListProvider onClose={close}>
      <Popover
        position="bottom-start"
        opened={opened}
        onChange={open}
        closeOnClickOutside
        closeOnEscape
        defaultOpened
        width={popoverWidth}
        withinPortal={withinPortal}
      >
        <Popover.Target>
          {customTarget ? (
            <div ref={setCustomTargetRef} onClick={toggle}>
              {customTarget}
            </div>
          ) : (
            <Input
              label={label}
              onClick={toggle}
              ref={setInputRef}
              count={count}
            />
          )}
        </Popover.Target>

        <Popover.Dropdown p={0}>
          <Stack ref={setDropdownRef} spacing="02" p="02">
            {children}
          </Stack>
        </Popover.Dropdown>
      </Popover>
    </CheckBoxListProvider>
  );
}

type InputProps = {
  label: string;
  onClick: () => void;
  count: number;
};

const Input = forwardRef<HTMLButtonElement, InputProps>(
  ({ label, onClick, count }, ref) => {
    return (
      <Button
        ref={ref}
        variant="outline"
        color="gray"
        size="xs"
        rightIcon={<IconSelector size={13} />}
        onClick={onClick}
      >
        <Group noWrap spacing="02">
          <Text fw={400} size="xs" c="gray.6">
            {label}
          </Text>
          <Counter value={count} size="xs" />
        </Group>
      </Button>
    );
  },
);

Input.displayName = 'Input';

type CheckboxItemSimpleProps = {
  checkBoxData: CheckBoxData;
  value: string;
  onChange: (newValue: string, checked: boolean) => void;
  checked: boolean;
};

export function CheckboxItemSimple({
  value,
  checked,
  onChange,
  checkBoxData,
}: CheckboxItemSimpleProps) {
  const styles = (theme: MantineTheme) => ({
    root: {
      '&:hover': { backgroundColor: theme.colors.gray[1] },
    },
    labelWrapper: {
      width: '100%',
    },
    label: {
      paddingLeft: theme.spacing['02'],
    },
  });

  return (
    <Checkbox
      p="02"
      styles={styles}
      label={checkBoxData.label}
      value={value}
      checked={checked}
      onChange={event => onChange(value, event.target.checked)}
    />
  );
}

type CheckboxItemWithChildrenProps = {
  checkBoxData: CheckBoxDataWithChildren;
  values: string[];
  onChange: (value: string[]) => void;
};

export function CheckboxItemWithChildren({
  values,
  onChange,
  checkBoxData,
}: CheckboxItemWithChildrenProps) {
  const parentCheckedStatus = checkBoxData.children?.every(child =>
    values.includes(child.value),
  );

  const parentIndeterminateStatus =
    checkBoxData.children?.some(child => values.includes(child.value)) &&
    !parentCheckedStatus;

  const handleChange = (newValues: string[], checked: boolean) => {
    if (checked) {
      onChange([...values.filter(v => !newValues.includes(v)), ...newValues]);
    } else {
      onChange(values.filter(v => !newValues.includes(v)));
    }
  };

  const styles = (theme: MantineTheme) => ({
    root: {
      '&:hover': { backgroundColor: theme.colors.gray[1] },
    },
    labelWrapper: {
      width: '100%',
    },
    label: {
      paddingLeft: theme.spacing['02'],
    },
  });

  return (
    <Accordion
      defaultValue={checkBoxData.value}
      styles={{
        label: {
          padding: 0,
        },
        control: {
          padding: 0,
        },
        item: {
          border: 'none',
        },
        content: {
          padding: 0,
        },
      }}
    >
      <Accordion.Item value={checkBoxData.value}>
        <Accordion.Control>
          <Group noWrap spacing="00">
            <Checkbox
              p="02"
              styles={styles}
              value={checkBoxData.value}
              indeterminate={parentIndeterminateStatus}
              transitionDuration={0}
              checked={parentCheckedStatus}
              onChange={event => {
                handleChange(
                  checkBoxData.children?.map(child => child.value) || [],
                  event.target.checked,
                );
              }}
              onClick={event => {
                event.stopPropagation();
              }}
            />

            <Text
              size="sm"
              c="gray.8"
              fw={500}
              w="100%"
              onClick={event => {
                event.stopPropagation();
                handleChange(
                  checkBoxData.children?.map(child => child.value) || [],
                  !parentCheckedStatus,
                );
              }}
            >
              {checkBoxData.label}
            </Text>
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          {checkBoxData.children.map(item => (
            <Checkbox
              p="02"
              styles={styles}
              key={item.value}
              label={item.label}
              value={item.value}
              ml="05"
              checked={values.includes(item.value)}
              onChange={event =>
                handleChange([item.value], event.target.checked)
              }
            />
          ))}
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}

type DropdownFooterProps = {
  onReset: () => void;
};

export function DropdownFooter({ onReset }: DropdownFooterProps) {
  const { t } = useTranslation('common');
  const { onClose } = useCheckBoxList();

  return (
    <Group noWrap spacing="02" position="right">
      <Button variant="subtle" size="sm" color="gray" onClick={onReset}>
        {t('reset')}
      </Button>
      <Button variant="light" size="sm" onClick={onClose}>
        {t('apply')}
      </Button>
    </Group>
  );
}
